import React, { useEffect, useState } from 'react';
import { app, authentication } from '@microsoft/teams-js';
import {
    Dropdown,
    Textarea,
    Button,
    Option,
    Label,
    Spinner,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { submitDialog } from '../../utils/teams';
import { IBotSubmitPayload } from '../../model/IBotSubmitPayload';
import BotSubmitAction from '../../model/BotSubmitAction';
import { useResolveConversationPageStyles } from './ResolveConversationPage.styles';
import { useSearchParams } from 'react-router';
import { dialog } from '@microsoft/teams-js';
import { resolveActiveConversationsMessage } from '../../store/features/activeConversations/activeConversationsAPI';

const ResolveConversationPage = () => {
    const { t } = useTranslation();
    const styles = useResolveConversationPageStyles();
    const [searchParams] = useSearchParams();

    const brokerConnectionId = searchParams.get('brokerConnectionId');
    const fromToolbar = searchParams.get('fromToolbar') === 'true';

    const [rating, setRating] = useState<string>('');
    const [comment, setComment] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        app.notifySuccess();
    }, []);

    const isLowRating = !!rating && parseInt(rating) <= 3;
    const isSubmitDisabled =
        !rating || isSubmitting || (isLowRating && !comment.trim());

    const handleSubmit = async () => {
        if (isSubmitting || !rating) return;
        setIsSubmitting(true);

        if (fromToolbar) {
            try {
                const response = await resolveActiveConversationsMessage(
                    brokerConnectionId!,
                    parseInt(rating),
                    comment?.trim() || null
                );

                if (response.status >= 200 && response.status < 300) {
                    dialog.url.submit({ success: true });
                } else {
                    const errorText =
                        (await response.data?.toString()) ?? 'unknown';
                    console.error('API error response:', errorText);
                    throw new Error(
                        `Failed to resolve query: ${response.status} - ${errorText}`
                    );
                }
            } catch (error) {
                console.error('Error submitting rating:', error);
                setIsSubmitting(false);
            }
        } else {
            const token = await authentication.getAuthToken();

            const submitRequest: IBotSubmitPayload<any> = {
                data: {
                    BrokerConnectionId: brokerConnectionId,
                    groundTeamRating: rating,
                    groundTeamRatingComment: comment?.trim() || null,
                },
                appUserAccessToken: token,
                action: BotSubmitAction.ResolveConversation,
            };
            submitDialog(submitRequest);
        }
    };

    return (
        <div className={styles.root}>
            <div className={styles.formField}>
                <Label weight="semibold" required>
                    {t('ResolveConversation_RateConversation')}
                </Label>
                <Dropdown
                    placeholder={t('ResolveConversation_RatingPlaceholder')}
                    value={
                        rating
                            ? `${rating} star${parseInt(rating) > 1 ? 's' : ''}`
                            : ''
                    }
                    selectedOptions={rating ? [rating] : []}
                    onOptionSelect={(_, data) =>
                        setRating(data.optionValue as string)
                    }
                >
                    <Option value="5">
                        {t('ResolveConversation_ExcellentRating')}
                    </Option>
                    <Option value="4">
                        {t('ResolveConversation_GoodRating')}
                    </Option>
                    <Option value="3">
                        {t('ResolveConversation_AverageRating')}
                    </Option>
                    <Option value="2">
                        {t('ResolveConversation_PoorRating')}
                    </Option>
                    <Option value="1">
                        {t('ResolveConversation_VeryPoorRating')}
                    </Option>
                </Dropdown>
            </div>

            {rating && (
                <div className={styles.formField}>
                    <Label weight="semibold" required={isLowRating}>
                        {isLowRating
                            ? t('ResolveConversation_CommentsRequired')
                            : t('ResolveConversation_AdditionalComments')}
                    </Label>
                    {isLowRating && (
                        <div className={styles.helperText}>
                            {t('ResolveConversation_LowRatingHelper')}
                        </div>
                    )}
                    <Textarea
                        rows={4}
                        className={styles.textArea}
                        value={comment}
                        maxLength={1000}
                        placeholder={t(
                            'ResolveConversation_CommentsPlaceholder'
                        )}
                        onChange={(_, data) => setComment(data?.value || '')}
                        required={isLowRating}
                    />
                </div>
            )}

            <div className={styles.warningText}>
                {t('ResolveConversation_ResolveWarning')}
            </div>

            <div className={styles.actions}>
                <Button
                    iconPosition="before"
                    icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
                    appearance="primary"
                    disabled={isSubmitDisabled}
                    onClick={handleSubmit}
                >
                    {t('ResolveConversation_ConfirmResolve')}
                </Button>
            </div>
        </div>
    );
};

export default ResolveConversationPage;
